import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { webgpuKit } from '../render/backend'
import { FARM_EXPANSION_CONFIG, type GardenBounds } from '../game/farm-expansion'
import type { BalloonAnimalId } from './animal-catalog'
import { createCapturePresentation, type CapturePresentation } from './balloon-capture'
import { applyHeartEyeMaterial, clearHeartEyes, heartEyeCount as countHeartEyes, installHeartEyeShader, setHeartEyes } from './animal-eyes'
import { lowestClipPoseY } from './animal-grounding'
import { nearestRayHit } from '../scene/skinned-pick'
import { cloneMerged, objectBounds, skeletonsOf } from '../scene/merge-static-meshes'
import { mergeAnimalParts } from './animal-batching'
import { stageHasHeartEyes, type AnimalStage } from '../game/animal-conditions'
import { canSellAnimal, startsCaptured } from '../game/sales'
import { carnivalRing } from '../game/animal-travel'
import { requestStage, tickResidency } from '../game/animal-residency'
import { headingOf, beginSimStep, createAnimalSimRecord, drainSimEvents, holdAnimalSim, interpolateAnimalSim, pushAnimalSim, setSimAlarmed, setSimPursuit, snapAnimalSim, type AnimalPursuit, type AnimalSimRecord, type AnimalSleepSpot } from '../game/animal-sim'
import { stepGateTravel, stepHomeTrip, stepPursuit, stepSleep, tickVisitCooldown } from '../game/animal-routes'
import { ALARM_SPEED_SCALE, chooseWanderTarget, stepWander, type WanderContext } from '../game/animal-wander'
import { seededRandom } from '../game/seeded-random'

export type AnimalClip = 'IDLE' | 'WALK' | 'SLEEP' | 'EAT' | 'STRIKE'
export type AnimalAppearance = 'standard' | 'wild'
export type { BalloonAnimalId } from './animal-catalog'

type AnimalGLTF = GLTF & { readonly animations: THREE.AnimationClip[] }
type AnimalMaterial = THREE.Material & { color?: THREE.Color; roughness?: number; metalness?: number; clearcoat?: number; clearcoatRoughness?: number }

export interface BalloonAnimalOptions {
  readonly id: BalloonAnimalId
  readonly instanceId?: string
  readonly growthScale?: number
  readonly onDetailedModelReady?: () => void
  readonly assetUrl: string
  readonly appearance?: AnimalAppearance
  readonly name: string
  readonly spawn: readonly [number, number]
  readonly groundY: number
  readonly seed: number
  readonly size: number
  readonly speed: number
  readonly bounds: { readonly x: number; readonly z: number }
  /** Current expandable plot limits; stationary-wanderer games may omit it. */
  readonly getGardenBounds?: () => GardenBounds
  /** Ignore animal clicks when an in-game HUD panel is occupying the pointer. */
  readonly isPointerBlocked?: (clientX: number, clientY: number) => boolean
  readonly canvas: HTMLCanvasElement
  readonly camera: THREE.Camera
  readonly captureOnClick?: boolean
  /** Accent tint for this species' non-heart cosmetics; hearts are always pink. */
  readonly eyeColor?: string
  /** Starting rung on the condition ladder. */
  readonly stage?: AnimalStage
  /**
   * How far the animal is allowed to wander, and whether it is loose at the
   * carnival or fenced into the plot. Stage 1 (up at the tents) is loose; from
   * stage 2 on it is clamped to the farm, which is what physically expresses
   * "visited the farm, inside the carnival".
   */
  readonly isLoose?: () => boolean
  /** Half-extents to roam when loose at the carnival. Defaults to the whole meadow ring. */
  readonly carnivalBounds?: { readonly x: number; readonly z: number }
  /**
   * Where the rest of this animal pack is, while it is out at the carnival.
   * Null when it came alone, is the pack leader, or its pack has moved on.
   */
  readonly getPackAnchor?: () => { readonly x: number; readonly z: number } | null
  /** Terrain height at garden (x, z); enables walking over deformed ground. */
  readonly groundSampler?: (x: number, z: number) => number
  /**
   * A flier does not wander. The predator sim places it every frame through
   * `setFlightPose`, and it is only ever drawn as its full model.
   */
  readonly flier?: boolean
}

/** One frame of flight, as decided by `src/game/predator.ts`. */
export interface FlightPose {
  readonly visible: boolean
  readonly x: number
  readonly y: number
  readonly z: number
  /** Yaw about +Y for a +X-forward model. */
  readonly heading: number
  /** Nose-down angle in radians. */
  readonly pitch: number
  /** IDLE is the folded, perched pose; WALK is the wide-winged flight cycle. */
  readonly clip: AnimalClip
  /** Animation speed. A balloon barely flaps, so a patrol runs well under 1. */
  readonly rate: number
  /** How full of helium the balloon is, 0..1. A slack owl is visibly smaller. */
  readonly puff?: number
}

/** Where a night animal curls up for the day, and which way it faces (yaw about +Y, +X-forward model). */
export type SleepSpot = AnimalSleepSpot

export interface BalloonAnimal {
  readonly id: BalloonAnimalId
  readonly instanceId: string
  readonly root: THREE.Group
  readonly isSold: boolean
  readonly hasDetailedModel: boolean
  readonly currentPosition: THREE.Vector3
  readonly currentHeading: number
  readonly currentScale: number
  /** World height of the ground under the animal (the root rides above it while a flier flies or a capture lifts). */
  readonly groundHeight: number
  /** Half the animal's longest side in world units, growth included: how wide its contact shadow reads. */
  readonly footprintRadius: number
  readonly renderPriority: number
  /** True for a species that is positioned by the predator sim instead of wandering. */
  readonly isFlier: boolean
  /**
   * The simulation record behind this animal. The view reads it; only
   * `tickSim`, collision (`pushSim`) and the command setters below change it.
   */
  readonly sim: Readonly<AnimalSimRecord>
  /**
   * Advance the simulation by one fixed step (see game/sim-clock.ts): wandering, the
   * walks, the bed, hunts' steering and the stage. One call per step, from the sim world tick.
   */
  tickSim(stepSeconds: number): void
  /** Push the sim record sideways on this tick only (collision); the view blends the change in. */
  pushSim(dx: number, dz: number): void
  /** Whether the flier is currently in the world at all (it is away by day). */
  readonly flightVisible: boolean
  /** Place a flier for this frame. No-op for ground animals. */
  setFlightPose(pose: FlightPose): void
  /** Prey in a panic: runs faster in short dashes and beats its wings harder. */
  setAlarmed(alarmed: boolean): void
  readonly isAlarmed: boolean
  /**
   * Walk to `spot` and curl up there, or wake and wander again when null. There is no
   * sleep clip: the model keeps its `IDLE` pose, slowed, and is squashed into a crouch
   * with a slow breath, so a new species needs no extra animation.
   */
  setSleepSpot(spot: SleepSpot | null): void
  /** True once it has reached its spot and is curled up (not merely on its way there). */
  readonly isSleeping: boolean
  /**
   * Walk to a house door to go indoors, or carry on wandering when null. Takes
   * priority over sleeping. A flier ignores it: it is simply shown or hidden.
   * `speedScale` above 1 is a sprint, e.g. a mouse fleeing a snake.
   */
  setHomeTrip(door: { readonly x: number; readonly z: number } | null, speedScale?: number): void
  /** True once it has reached the door it was sent to. */
  readonly isAtDoor: boolean
  /** True while it walks to a house door. */
  readonly isGoingHome: boolean
  /**
   * Steer toward a point at `speedScale` times its walking speed, as a visitor
   * crossing the meadow does; 0 holds it still. Null hands it back to its own
   * wandering. A house trip takes priority. A flier ignores it. Hunts steer the
   * sim record directly (game/animal-hunt.ts); this is the command for the rest.
   */
  setPursuit(pursuit: AnimalPursuit | null): void
  facePoint(x: number, z: number): void
  setEating(eating: boolean, holdingBerry?: boolean): void
  readonly isEating: boolean
  /** Stand at a point, e.g. a house door it has just stepped out of, and wander from there. */
  placeAt(x: number, z: number): void
  readonly isLoose: boolean
  /** The look currently worn; residency is gated on actually walking inside. */
  readonly appearance: AnimalAppearance
  readonly animationPhase: number
  /** Keep a chosen detailed instance during LOD swaps without losing state. */
  setDetailedVisible(visible: boolean): void
  readonly canSell: boolean
  sell(): boolean
  readonly gltf: AnimalGLTF | null
  setAnimation(name: AnimalClip, fadeSeconds?: number): void
  /**
   * Play a clip once from its start (the mountain lion's `STRIKE`), over whatever its
   * movement would pick, then hand back to that. Facing still follows the sim. False
   * when the model is not drawn or has no such clip; the sim goes on either way.
   */
  playOnce(name: AnimalClip): boolean
  setAppearance(appearance: AnimalAppearance): void
  beginCapture(): boolean
  readonly isCaptured: boolean
  readonly isCapturing: boolean
  readonly captureProgress: number
  /** True once the router has carried this animal onto the farm side of the gate. */
  readonly isAtFarm: boolean
  /** Residency decided, but still waiting for the walk-in and browse time. */
  readonly isResidencyPending: boolean
  /**
   * Where this animal sits on the four-condition ladder. Drives appearance and
   * heart eyes, and is the single thing the progression engine pokes.
   */
  stage: AnimalStage
  /** Species accent colour, for debug readouts; heart eyes use the shared pink. */
  readonly eyeColor: string
  /** How many heart eyes are currently worn, for the debug report. */
  readonly heartEyeCount: number
  setGrowth(scale: number): void
  /**
   * Draw a frame. `simAlpha` is how far the display is between the last sim
   * tick and the next; the position is blended by it. The view never steps the sim.
   */
  update(deltaSeconds: number, simAlpha: number): void
  dispose(): void
}

interface LoadedAnimal {
  readonly gltf: AnimalGLTF
  readonly root: THREE.Group
  readonly mixer: THREE.AnimationMixer
  readonly actions: Map<AnimalClip, THREE.AnimationAction>
  activeAnimation: AnimalClip | null
}

const BODY_MATERIALS: Record<BalloonAnimalId, THREE.MeshStandardMaterial> = {
  pig: new THREE.MeshStandardMaterial({ color: '#ed679d', roughness: 0.28, metalness: 0.02 }),
  sheep: new THREE.MeshStandardMaterial({ color: '#fff0d0', roughness: 0.62 }),
  cow: new THREE.MeshStandardMaterial({ color: '#fff0d0', roughness: 0.42 }),
  chicken: new THREE.MeshStandardMaterial({ color: '#f7c94f', roughness: 0.32, metalness: 0.01 }),
  duck: new THREE.MeshStandardMaterial({ color: '#a95c3a', roughness: 0.3, metalness: 0.01 }),
  goose: new THREE.MeshStandardMaterial({ color: '#fff0d0', roughness: 0.38 }),
  frog: new THREE.MeshStandardMaterial({ color: '#6ab84e', roughness: 0.3, metalness: 0.01 }),
  owl: new THREE.MeshStandardMaterial({ color: '#a9774b', roughness: 0.28, metalness: 0.01 }),
  raccoon: new THREE.MeshStandardMaterial({ color: '#8f949b', roughness: 0.3, metalness: 0.01 }),
  mouse: new THREE.MeshStandardMaterial({ color: '#c99a6b', roughness: 0.3, metalness: 0.01 }),
  rat: new THREE.MeshStandardMaterial({ color: '#7b7480', roughness: 0.3, metalness: 0.01 }),
  snake: new THREE.MeshStandardMaterial({ color: '#3f9e6e', roughness: 0.26, metalness: 0.01 }),
  squirrel: new THREE.MeshStandardMaterial({ color: '#bd683c', roughness: 0.26, metalness: 0.01 }),
  mole: new THREE.MeshStandardMaterial({ color: '#5b5663', roughness: 0.3, metalness: 0.01 }),
  deer: new THREE.MeshStandardMaterial({ color: '#a8683c', roughness: 0.28, metalness: 0.01 }),
  starhorn: new THREE.MeshStandardMaterial({ color: '#bf9fe6', roughness: 0.28, metalness: 0.03 }),
  cougar: new THREE.MeshStandardMaterial({ color: '#c8955a', roughness: 0.28, metalness: 0.02 }),
}
/** An animal's collision footprint radius as a share of its catalog size (its longest side). */
const BODY_RADIUS_SHARE = 0.24
const HOOF_MATERIAL = new THREE.MeshStandardMaterial({ color: '#76505d', roughness: 0.31 })
const WILD_BALLOON_COLOR = new THREE.Color('#e53649')
const WILD_BALLOON_MATERIAL = new THREE.MeshPhysicalMaterial({
  name: 'Wild carnival balloon · uniform red mask',
  color: WILD_BALLOON_COLOR,
  roughness: 0.24,
  metalness: 0.015,
  clearcoat: 0.48,
  clearcoatRoughness: 0.17,
})
// The wild mask draws the merged eye mesh too: its hearts stay collapsed, so the mask shows pupils only.
// Installed on first use, not at module load: the module-load call stalled the farm's start-up.
let wildHeartHookInstalled = false

const STANDARD_MATERIALS = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>()
const WILD_MATERIALS = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>()
const TRANSITION_MATERIALS = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>()
const CAPTURE_MATERIAL_STATES = new WeakMap<THREE.Material, {
  readonly progress: { value: number }
  readonly bottom: { value: number }
  readonly top: { value: number }
}>()

interface CaptureVerticalRange {
  readonly bottom: number
  readonly top: number
}

function disposeMaterial(material: THREE.Material | THREE.Material[] | undefined): void {
  if (Array.isArray(material)) material.forEach(disposeMaterial)
  else material?.dispose()
}

function disposeTemporaryMaterials(mesh: THREE.Mesh): void {
  // The wild mask is a process-wide immutable material shared by all meshes;
  // only per-animal transition materials are owned and disposed here.
  const transition = TRANSITION_MATERIALS.get(mesh)
  if (Array.isArray(transition)) transition.forEach((material) => CAPTURE_MATERIAL_STATES.delete(material))
  else if (transition) CAPTURE_MATERIAL_STATES.delete(transition)
  disposeMaterial(transition)
  WILD_MATERIALS.delete(mesh)
  TRANSITION_MATERIALS.delete(mesh)
}

function setAnimalAppearance(root: THREE.Object3D, appearance: AnimalAppearance): void {
  if (appearance === 'wild' && !wildHeartHookInstalled) {
    wildHeartHookInstalled = true
    installHeartEyeShader(WILD_BALLOON_MATERIAL, false)
  }
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return
    if (appearance === 'wild') {
      const currentWild = WILD_MATERIALS.get(object)
      if (currentWild && object.material === currentWild) return
      const originals = STANDARD_MATERIALS.get(object) ?? object.material
      STANDARD_MATERIALS.set(object, originals)
      disposeTemporaryMaterials(object)
      const wild = Array.isArray(originals) ? originals.map(() => WILD_BALLOON_MATERIAL) : WILD_BALLOON_MATERIAL
      WILD_MATERIALS.set(object, wild)
      object.material = wild
    } else {
      const originals = STANDARD_MATERIALS.get(object)
      if (originals) object.material = originals
      // Only a mesh that wears hearts needs its material put back; the rest keep their originals.
      if (object.userData.heartEyes) applyHeartEyeMaterial(object)
      disposeTemporaryMaterials(object)
    }
  })
}

function createCaptureMaterial(source: THREE.Material, range: CaptureVerticalRange): THREE.MeshPhysicalMaterial {
  const standard = source as AnimalMaterial
  const material = source instanceof THREE.MeshPhysicalMaterial
    ? source.clone()
    : new THREE.MeshPhysicalMaterial().copy(source as THREE.MeshStandardMaterial)
  material.name = `${source.name || 'Animal material'} · paint-reveal transition`
  material.roughness = WILD_BALLOON_MATERIAL.roughness
  material.metalness = WILD_BALLOON_MATERIAL.metalness
  material.clearcoat = WILD_BALLOON_MATERIAL.clearcoat
  material.clearcoatRoughness = WILD_BALLOON_MATERIAL.clearcoatRoughness
  material.color.copy(standard.color ?? new THREE.Color('#ffffff'))
  // Painted parts keep their colour in vertex colours, which the reveal must still multiply in.
  material.vertexColors = source.vertexColors
  const textured = source as THREE.MeshStandardMaterial
  material.map = textured.map ?? null
  material.alphaMap = textured.alphaMap ?? null

  const state = {
    progress: { value: 0 },
    bottom: { value: range.bottom },
    top: { value: Math.max(range.top, range.bottom + 0.001) },
  }
  material.customProgramCacheKey = () => 'balloon-paint-reveal-v1'
  CAPTURE_MATERIAL_STATES.set(material, state)
  const nodeKit = webgpuKit()
  if (nodeKit) {
    nodeKit.applyPaintReveal(material, state, WILD_BALLOON_COLOR)
    return material
  }
  const previousCompile = material.onBeforeCompile
  material.onBeforeCompile = (shader, renderer) => {
    previousCompile(shader, renderer)
    shader.uniforms.uCaptureProgress = state.progress
    shader.uniforms.uCaptureBottom = state.bottom
    shader.uniforms.uCaptureTop = state.top
    shader.uniforms.uCaptureWildColor = { value: WILD_BALLOON_COLOR }
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
varying float vCaptureWorldY;
varying vec3 vCaptureWorldPosition;`)
      .replace('#include <project_vertex>', `vec4 captureWorldPosition = modelMatrix * vec4(transformed, 1.0);
vCaptureWorldY = captureWorldPosition.y;
vCaptureWorldPosition = captureWorldPosition.xyz;
#include <project_vertex>`)
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform float uCaptureProgress;
uniform float uCaptureBottom;
uniform float uCaptureTop;
uniform vec3 uCaptureWildColor;
varying float vCaptureWorldY;
varying vec3 vCaptureWorldPosition;`)
      .replace('#include <color_fragment>', `#include <color_fragment>
float captureHeight = clamp((vCaptureWorldY - uCaptureBottom) / max(uCaptureTop - uCaptureBottom, 0.001), 0.0, 1.0);
float captureEdge = 1.03 - uCaptureProgress * 1.06;
float captureNoise = (sin(vCaptureWorldPosition.x * 9.0 + vCaptureWorldPosition.z * 6.0) + sin(vCaptureWorldPosition.z * 13.0 - vCaptureWorldPosition.y * 5.0)) * 0.012;
float captureReveal = smoothstep(captureEdge - 0.035, captureEdge + 0.035, captureHeight + captureNoise);
float captureShimmer = 1.0 - smoothstep(0.012, 0.055, abs(captureHeight + captureNoise - captureEdge));
diffuseColor.rgb = mix(uCaptureWildColor, diffuseColor.rgb, captureReveal);
diffuseColor.rgb += vec3(0.055, 0.04, 0.025) * captureShimmer * (1.0 - uCaptureProgress);`)
  }
  return material
}

function setAnimalAppearanceProgress(root: THREE.Object3D, progress: number, range: CaptureVerticalRange): void {
  const amount = THREE.MathUtils.clamp(progress, 0, 1)
  if (amount >= 1) {
    setAnimalAppearance(root, 'standard')
    return
  }

  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return
    const originals = STANDARD_MATERIALS.get(object)
    if (!originals) return
    let transition = TRANSITION_MATERIALS.get(object)
    if (!transition) {
      transition = Array.isArray(originals)
        ? originals.map((material) => createCaptureMaterial(material, range))
        : createCaptureMaterial(originals, range)
      TRANSITION_MATERIALS.set(object, transition)
    }

    const sourceArray = Array.isArray(originals) ? originals : [originals]
    const transitionArray = Array.isArray(transition) ? transition : [transition]
    transitionArray.forEach((material, index) => {
      const state = CAPTURE_MATERIAL_STATES.get(material)
      if (state) {
        state.progress.value = amount
        state.bottom.value = range.bottom
        state.top.value = Math.max(range.top, range.bottom + 0.001)
      }
      const source = sourceArray[index] as AnimalMaterial
      const target = material as AnimalMaterial
      if (target.roughness !== undefined) {
        target.roughness = THREE.MathUtils.lerp(WILD_BALLOON_MATERIAL.roughness, source.roughness ?? 0.7, amount)
      }
      if (target.metalness !== undefined) {
        target.metalness = THREE.MathUtils.lerp(WILD_BALLOON_MATERIAL.metalness, source.metalness ?? 0.02, amount)
      }
      if (target.clearcoat !== undefined) {
        target.clearcoat = THREE.MathUtils.lerp(WILD_BALLOON_MATERIAL.clearcoat, source.clearcoat ?? 0, amount)
      }
      target.needsUpdate = true
    })
    object.material = transition
  })
}

/** Bounds of `object` in `root`'s space, following merged parts into their current pose. */
function getLocalBounds(root: THREE.Group, object: THREE.Object3D): THREE.Box3 {
  return objectBounds(object, root)
}

const animalAssetCache = new Map<string, Promise<AnimalGLTF>>()

function loadModel(url: string): Promise<AnimalGLTF> {
  const existing = animalAssetCache.get(url)
  if (existing) return existing
  const loading = new Promise<AnimalGLTF>((resolve, reject) => {
    new GLTFLoader().load(url, (gltf) => {
      mergeAnimalParts(gltf.scene, gltf.animations)
      resolve(gltf as AnimalGLTF)
    }, undefined, reject)
  })
  animalAssetCache.set(url, loading)
  void loading.catch(() => {
    if (animalAssetCache.get(url) === loading) animalAssetCache.delete(url)
  })
  return loading
}

function makePlaceholder(parent: THREE.Group, id: BalloonAnimalId): void {
  const color = BODY_MATERIALS[id]
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.72, 32, 24), color)
  body.scale.set(1.3, 0.92, 0.82)
  body.position.set(0, 0.96, 0)
  body.castShadow = true
  parent.add(body)
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.48, 32, 24), color)
  head.position.set(0.78, 1.42, 0)
  head.castShadow = true
  parent.add(head)
  const snout = new THREE.Mesh(new THREE.SphereGeometry(0.26, 24, 16), color)
  snout.position.set(1.12, 1.27, 0)
  parent.add(snout)
  for (const [x, z] of [[-0.56, -0.42], [0.48, -0.42], [-0.56, 0.42], [0.48, 0.42]]) {
    const hoof = new THREE.Mesh(new THREE.SphereGeometry(0.16, 18, 12), HOOF_MATERIAL)
    hoof.position.set(x, 0.14, z)
    hoof.castShadow = true
    parent.add(hoof)
  }
}

export async function createBalloonAnimal(parent: THREE.Group, options: BalloonAnimalOptions): Promise<BalloonAnimal> {
  const wrapper = new THREE.Group()
  wrapper.name = `Balloon ${options.name} · wandering character`
  wrapper.scale.setScalar(options.growthScale ?? 1)
  const instanceId = options.instanceId ?? options.id
  wrapper.position.set(options.spawn[0], options.groundY, options.spawn[1])
  parent.add(wrapper)

  const posePivot = new THREE.Group()
  posePivot.name = `${options.name} · capture flourish pivot`
  wrapper.add(posePivot)

  // Eased ground height so stepping over dug mounds reads as a gentle bob.
  let groundYCurrent = options.groundY

  let loaded: LoadedAnimal | null = null
  let modelRoot: THREE.Group | null = null
  let gltf: AnimalGLTF | null = null
  let modelLoading: Promise<void> | null = null
  let placeholderLoaded = false
  let appearance = options.appearance ?? 'wild'
  let active: AnimalClip = 'WALK'
  let elapsed = 0
  // Built already settled (a restored save, an animal leaving its house): it was captured before, so it is sellable and plays no reveal.
  let captured = startsCaptured(appearance)
  let sold = false
  let focused = false
  /** The alarm the mixer rate last followed, so a hunt panicking the sim record reaches the clips. */
  let shownAlarmed = false
  let wasPursuing = false
  let eating = false
  let holdingBerry = false
  let heldBerry: THREE.Object3D | null = null
  let flightRate = 1
  let sleepBlend = 0
  /** Seconds left of a clip played once (`playOnce`); while it runs the movement does not pick the clip. */
  let oneShotLeft = 0
  // Shared by every walk, chase and wander branch; each one uses it at once, so
  // one scratch quaternion per animal replaces a fresh allocation per frame.
  const facingScratch = new THREE.Quaternion()
  let flightVisible = !options.flier
  const alarmScale = (): number => (sim.alarmed ? ALARM_SPEED_SCALE : 1)
  const hasSleepClip = (): boolean => Boolean(loaded?.actions.has('SLEEP'))
  /** Ease the crouch in or out and breathe; only the pivot is touched, never the wrapper. */
  const applySleepPose = (delta: number): void => {
    sleepBlend = THREE.MathUtils.clamp(sleepBlend + (sim.asleep ? delta : -delta * 1.5) / 1.4, 0, 1)
    const eased = sleepBlend * sleepBlend * (3 - 2 * sleepBlend)
    // A species with a SLEEP clip lies down by itself; only the others are crouched in code.
    if (hasSleepClip()) return
    const breath = Math.sin(elapsed * 1.7) * 0.018 * eased
    const squash = 1 - 0.26 * eased + breath
    posePivot.scale.set(1 + 0.07 * eased, squash, 1 + 0.07 * eased)
    // Keep the paws on the grass: scaling about the pivot would otherwise lift the model.
    posePivot.position.set(pivotBasePosition.x, pivotBasePosition.y * squash, pivotBasePosition.z)
  }
  let detailActive = false
  let pendingCapture = false
  const stableAnimationPhase = seededRandom(options.seed ^ 0x51f15e)() * Math.PI * 2
  let capture: CapturePresentation | null = null
  let captureVerticalRange: CaptureVerticalRange | null = null
  let lastCaptureProgress = 0
  const pivotBasePosition = new THREE.Vector3()
  const pivotBaseRotation = new THREE.Euler()
  const random = seededRandom(options.seed)
  // The simulation half of this animal. Wander and walk movement lives in it;
  // the wrapper below only shows it (see docs/sim-view-split.md).
  const sim = createAnimalSimRecord({
    x: options.spawn[0],
    z: options.spawn[1],
    motion: 'walk',
    held: Boolean(options.flier),
    radius: options.size * BODY_RADIUS_SHARE * (options.growthScale ?? 1),
    side: (options.stage ?? 1) >= 2 ? 'farm' : 'carnival',
    stage: options.stage ?? 1,
  })
  /** A scratch point for the blend. */
  const blended = { x: sim.x, z: sim.z }
  const upAxis = new THREE.Vector3(0, 1, 0)
  wrapper.userData.animalInstanceId = instanceId

  const ensureDetailedModel = (): Promise<void> => {
    if (loaded || placeholderLoaded) return Promise.resolve()
    if (modelLoading) return modelLoading
    modelLoading = (async () => {
      try {
        const asset = await loadModel(options.assetUrl)
        gltf = asset
        // Parsed geometry/textures are shared per species; only the hierarchy
        // and mixer are cloned when this particular animal enters close LOD.
        // Skinned parts must be cloned with their skeleton rebound to the clone's nodes.
        modelRoot = cloneMerged(asset.scene)
        modelRoot.name = `Original balloon ${options.name} · Blender GLB clone`
        const dimensions = objectBounds(modelRoot).getSize(new THREE.Vector3())
        const longestSide = Math.max(dimensions.x, dimensions.y, dimensions.z)
        if (!Number.isFinite(longestSide) || longestSide < 0.1) throw new Error('GLB has no measurable geometry')
        modelRoot.scale.setScalar(options.size / longestSide)
        modelRoot.position.set(0, 0, 0)
        modelRoot.rotation.set(0, 0, 0)
        modelRoot.updateMatrixWorld(true)
        const initialBounds = objectBounds(modelRoot)
        const modelCenter = initialBounds.getCenter(new THREE.Vector3())
        modelRoot.position.set(-modelCenter.x, -initialBounds.min.y, -modelCenter.z)
        modelRoot.updateMatrixWorld(true)
        const mixer = new THREE.AnimationMixer(modelRoot)
        const actions = new Map<AnimalClip, THREE.AnimationAction>()
        for (const clip of asset.animations) {
          const normalizedName = clip.name.toUpperCase()
          const kind = normalizedName.includes('WALK') ? 'WALK' : normalizedName.includes('IDLE') ? 'IDLE' : normalizedName.includes('SLEEP') ? 'SLEEP' : normalizedName.includes('STRIKE') ? 'STRIKE' : normalizedName.includes('EAT') ? 'EAT' : null
          if (kind) actions.set(kind, mixer.clipAction(clip))
        }
        loaded = { gltf: asset, root: modelRoot, mixer, actions, activeAnimation: null }
        heldBerry = modelRoot.getObjectByName('SQUIRREL_RIG_held_berry') ?? null
        if (heldBerry) heldBerry.scale.setScalar(holdingBerry ? 1 : 0.0001)
        posePivot.add(modelRoot)
        wrapper.updateMatrixWorld(true)
        const animalBounds = getLocalBounds(wrapper, modelRoot)
        const center = animalBounds.getCenter(new THREE.Vector3())
        posePivot.position.copy(center)
        modelRoot.position.sub(center)
        wrapper.updateMatrixWorld(true)
        // Ground on the lowest point the clips reach, not the bind pose (see lowestClipPoseY).
        const detailRoot: THREE.Group = modelRoot
        posePivot.position.y -= lowestClipPoseY(detailRoot, mixer, asset.animations, () => getLocalBounds(wrapper, detailRoot).min.y)
        if (options.flier) {
          // A flier has to sit exactly on its perch, so ground the pose it will
          // actually wear: the clips lift the rig to its standing height, which the
          // static model does not show.
          const perched = actions.get('IDLE')
          if (perched) {
            perched.reset().play()
            mixer.update(0)
            wrapper.updateMatrixWorld(true)
            posePivot.position.y -= getLocalBounds(wrapper, modelRoot).min.y
            perched.stop()
          }
        }
        pivotBasePosition.copy(posePivot.position)
        pivotBaseRotation.copy(posePivot.rotation)
        modelRoot.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            object.castShadow = false
            object.receiveShadow = false
          }
        })
        if (appearance === 'wild') setAnimalAppearance(modelRoot, 'wild')
        if (sim.stage >= 4) setHeartEyes(modelRoot)
        if (active !== 'IDLE') {
          const action = actions.get(active)
          if (action) action.play()
        }
        mixer.setTime(elapsed)
        if (!detailActive) posePivot.remove(modelRoot)
        console.info(`[Animal Balloon Farm] ${options.name} asset`, JSON.stringify({
          dimensions: dimensions.toArray().map((value) => Number(value.toFixed(2))),
          clips: asset.animations.map(({ name, duration }) => ({ name, duration: Number(duration.toFixed(2)) })),
        }))
      } catch (error) {
        console.error(`[Animal Balloon Farm] Could not load ${options.assetUrl}`, error)
        modelRoot = posePivot
        makePlaceholder(posePivot, options.id)
        const placeholderBounds = getLocalBounds(wrapper, posePivot)
        posePivot.position.copy(placeholderBounds.getCenter(new THREE.Vector3()))
        posePivot.children.forEach((child) => child.position.sub(posePivot.position))
        const groundedBounds = getLocalBounds(wrapper, posePivot)
        posePivot.position.y -= groundedBounds.min.y
        pivotBasePosition.copy(posePivot.position)
        placeholderLoaded = true
        setAnimalAppearance(posePivot, appearance)
      }
      if (detailActive) wrapper.visible = sim.stage > 0 && !sold
      options.onDetailedModelReady?.()
      if (pendingCapture) {
        pendingCapture = false
        appearance = 'wild'
        beginCapture()
      }
    })().finally(() => { modelLoading = null })
    return modelLoading
  }
  /** The ground point the animal stands on: its sim record, or the wrapper for a flier the predator sim places. */
  const standingPoint = (): { readonly x: number; readonly z: number } => (options.flier ? wrapper.position : sim)

  const gardenBoundsNow = (): GardenBounds => options.getGardenBounds?.() ?? FARM_EXPANSION_CONFIG.startBounds

  /** True while this animal lives out at the tents instead of inside the fence. */
  const isCarnivalSide = (): boolean => Boolean(options.isLoose?.())
    || sim.visit.side === 'carnival' || sim.visit.direction !== null

  /**
   * Where this animal is allowed to be right now.
   *
   * At the carnival it is loose and roams a wide ring around the tents. Once it
   * has visited the farm it is fenced to the plot, so "visiting the farm"
   * becomes something you can see happen rather than a flag flipping.
   */
  function leash(): { readonly x: number; readonly z: number } {
    const gardenBounds = options.getGardenBounds?.()
    if (isCarnivalSide()) {
      // The carnival is a wide ring of meadow around the plot, not a fixed patch:
      // as the farm grows the ring grows with it, so visitors can always roam a
      // long way out.
      const ring = carnivalRing(gardenBounds ?? FARM_EXPANSION_CONFIG.startBounds)
      return options.carnivalBounds
        ? { x: Math.max(options.carnivalBounds.x, ring.x), z: Math.max(options.carnivalBounds.z, ring.z) }
        : ring
    }
    const expansionX = Math.max(0, (gardenBounds?.halfWidth ?? FARM_EXPANSION_CONFIG.startBounds.halfWidth) - FARM_EXPANSION_CONFIG.startBounds.halfWidth)
    const expansionZ = Math.max(0, (gardenBounds?.halfDepth ?? FARM_EXPANSION_CONFIG.startBounds.halfDepth) - FARM_EXPANSION_CONFIG.startBounds.halfDepth)
    const halfWidth = Math.min(options.bounds.x + expansionX, Math.max(0, (gardenBounds?.halfWidth ?? options.bounds.x) - 1.2))
    const halfDepth = Math.min(options.bounds.z + expansionZ, Math.max(0, (gardenBounds?.halfDepth ?? options.bounds.z) - 1.2))
    return { x: halfWidth, z: halfDepth }
  }

  const wanderContext: WanderContext = {
    limitX: 0,
    limitZ: 0,
    carnival: false,
    alarmed: false,
    speed: options.speed,
    anchor: null,
    gardenBounds: FARM_EXPANSION_CONFIG.startBounds,
  }
  /** Gather what the wander system needs to know about the world right now. */
  const readWanderContext = (): WanderContext => {
    const limits = leash()
    const carnival = isCarnivalSide()
    wanderContext.limitX = limits.x
    wanderContext.limitZ = limits.z
    wanderContext.carnival = carnival
    wanderContext.alarmed = sim.alarmed
    wanderContext.anchor = carnival && !sim.alarmed ? options.getPackAnchor?.() ?? null : null
    wanderContext.gardenBounds = options.getGardenBounds?.() ?? FARM_EXPANSION_CONFIG.startBounds
    return wanderContext
  }
  const chooseTarget = (): void => chooseWanderTarget(sim, readWanderContext(), random)

  const setAnimation = (name: AnimalClip, fadeSeconds = 0.22): void => {
    if (active === name && (!loaded || loaded.activeAnimation === name)) return
    active = name
    if (!loaded || !detailActive) return
    const next = loaded.actions.get(name)
    if (!next) return
    const previous = loaded.activeAnimation ? loaded.actions.get(loaded.activeAnimation) : undefined
    next.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).fadeIn(fadeSeconds).play()
    previous?.fadeOut(fadeSeconds)
    loaded.activeAnimation = name
  }

  const playOnce = (name: AnimalClip): boolean => {
    const action = loaded?.actions.get(name)
    if (!loaded || !action || !detailActive || capture) return false
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    const previous = loaded.activeAnimation ? loaded.actions.get(loaded.activeAnimation) : undefined
    action.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).fadeIn(0.08).play()
    if (previous && previous !== action) previous.fadeOut(0.08)
    loaded.activeAnimation = name
    active = name
    // A sprint winds the walk up; the one-shot plays at its authored pace.
    loaded.mixer.timeScale = 1
    oneShotLeft = action.getClip().duration
    return true
  }

  chooseTarget()

  const beginCapture = (): boolean => {
    if (sold || capture || pendingCapture || appearance !== 'wild') return false
    if (!loaded && !placeholderLoaded) {
      pendingCapture = true
      focused = true
      void ensureDetailedModel()
      return true
    }
    captured = true
    appearance = 'standard'
    lastCaptureProgress = 0
    const bounds = getLocalBounds(posePivot, modelRoot ?? posePivot)
    const worldBounds = objectBounds(modelRoot ?? posePivot)
    captureVerticalRange = { bottom: worldBounds.min.y, top: worldBounds.max.y }
    oneShotLeft = 0
    // Initialize at progress zero so the animal stays glossy red until paint reaches it.
    setAnimalAppearanceProgress(modelRoot ?? posePivot, 0, captureVerticalRange)
    capture = createCapturePresentation(posePivot, wrapper, modelRoot, options.id, bounds, options.seed)
    focused = true
    setAnimation('IDLE', 0.22)
    // The reveal holds it still where it is drawn; the sim leaves it alone until the flourish ends.
    holdAnimalSim(sim, wrapper.position.x, wrapper.position.z)
    console.info(`[Animal Balloon Farm] ${options.name} capture flourish started`)
    return true
  }
  /** The flourish is over (or was cancelled): the sim has the animal again. A flier stays the predator sim's. */
  const releaseHold = (): void => {
    if (!options.flier) sim.held = false
  }
  const setAppearance = (nextAppearance: AnimalAppearance): void => {
    if (pendingCapture && nextAppearance === 'standard') return
    if (appearance === nextAppearance) return
    appearance = nextAppearance
    if (!modelRoot) return
    if (capture) {
      capture.dispose()
      capture = null
      captureVerticalRange = null
      releaseHold()
      posePivot.position.copy(pivotBasePosition)
      posePivot.rotation.copy(pivotBaseRotation)
    }
    setAnimalAppearance(modelRoot ?? posePivot, nextAppearance)
    appearance = nextAppearance
    if (nextAppearance === 'wild') {
      captured = false
      lastCaptureProgress = 0
    } else {
      captured = true
      lastCaptureProgress = 1
    }
  }

  /**
   * The heart-eyes half of the last condition. Kept separate from
   * `setAppearance` because it is keyed on the GLB's node names and has to be
   * reversible: the harness demotes a species to re-run the transition.
   */
  const applyHeartEyes = (wanted: boolean): void => {
    const root = modelRoot ?? posePivot
    if (wanted) {
      const converted = setHeartEyes(root)
      if (converted === 0) console.warn(`[Animal Balloon Farm] ${options.name} has no pupil nodes to convert to hearts`)
    } else {
      clearHeartEyes(root)
    }
  }

  /**
   * Play what the sim decided about the animal's rung (game/animal-residency.ts):
   * when to settle, and whether the settle waits for the walk in, are the sim's. The
   * look of it is here.
   *
   * The wild -> standard change reuses the existing 6.8s paint-reveal flourish
   * as its transition animation, because that flourish was already exactly
   * "this creature is committing to the farm" and rebuilding it would have
   * thrown away reviewed work. Reaching the final rung swaps the black pupils
   * for hearts, which is the only visual change in the game that is not a
   * material mask.
   */
  const playStageChange = (from: AnimalStage, next: AnimalStage): void => {
    const becomingResident = next >= 3 && from < 3
    const becomingWild = next <= 2 && from >= 3
    if (becomingResident) {
      // Play the reveal. beginCapture refuses while another is running, which
      // is what we want: a second settle arriving mid-flourish should queue on
      // the next stage change rather than snap.
      if (beginCapture()) {
        applyHeartEyes(stageHasHeartEyes(next))
        return
      }
    }
    if (becomingWild) setAppearance('wild')
    else if (next >= 3) setAppearance('standard')
    applyHeartEyes(stageHasHeartEyes(next))
  }

  const playSimEvents = (): void => {
    if (sim.events.length === 0) return
    drainSimEvents(sim, (event) => {
      if (event.type === 'stage-up' || event.type === 'stage-down') playStageChange(event.from, event.to)
    })
  }

  /** Command: move to a rung of the condition ladder. The sim decides when it takes effect. */
  const setStage = (next: AnimalStage): void => {
    requestStage(sim, next, standingPoint(), gardenBoundsNow(), Boolean(options.flier), random)
    playSimEvents()
  }

  const pressRaycaster = new THREE.Raycaster()
  const onPointerDown = (event: PointerEvent): void => {
    if (sold || options.captureOnClick === false || event.button !== 0 || event.detail >= 2
      || options.isPointerBlocked?.(event.clientX, event.clientY)
      || captured) return
    const bounds = options.canvas.getBoundingClientRect()
    const pointer = new THREE.Vector2(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    )
    pressRaycaster.setFromCamera(pointer, options.camera)
    if (nearestRayHit(pressRaycaster.ray, wrapper) === Infinity) return
    if (!beginCapture()) return
    event.preventDefault()
  }

  options.canvas.addEventListener('pointerdown', onPointerDown)

  function tickSim(stepSeconds: number): void {
    if (sold) return
    // A settle waiting for the walk in counts its time inside the plot, whatever else the animal is doing.
    if (sim.pendingStage !== null) {
      tickResidency(sim, stepSeconds, standingPoint(), gardenBoundsNow(), Boolean(options.flier), random)
      playSimEvents()
    }
    // Open the step even when held, so a push on a held animal blends in over one tick only.
    beginSimStep(sim)
    if (sim.held) return
    if (sim.home) {
      stepHomeTrip(sim, options.speed, stepSeconds)
    } else if (sim.pursuit) {
      stepPursuit(sim, options.speed, stepSeconds)
    } else if (sim.sleepSpot) {
      stepSleep(sim, options.speed, stepSeconds)
    } else {
      // Only stage-two visitors browse both sides of the gate: their rest counts down
      // while they are free, and ends in a walk through the gate.
      tickVisitCooldown(sim, stepSeconds, gardenBoundsNow(), Boolean(options.flier), sim.stage, random)
      if (sim.visit.route) stepGateTravel(sim, options.speed * alarmScale(), stepSeconds, sim.stage, random)
      else stepWander(sim, readWanderContext(), stepSeconds, random)
    }
  }

  return {
    id: options.id,
    instanceId,
    root: wrapper,
    get isSold(): boolean { return sold },
    get hasDetailedModel(): boolean { return Boolean(loaded || placeholderLoaded) },
    get currentPosition(): THREE.Vector3 { return wrapper.position },
    get currentHeading(): number { return wrapper.rotation.y },
    get currentScale(): number { return wrapper.scale.x },
    get groundHeight(): number { return groundYCurrent },
    get footprintRadius(): number { return options.size * 0.5 * wrapper.scale.x },
    get renderPriority(): number {
      return (focused ? 100 : 0) + (eating ? 80 : 0) + (capture ? 90 : 0) + (sim.stage >= 3 ? 10 : 0)
    },
    get isFlier(): boolean { return Boolean(options.flier) },
    sim,
    tickSim,
    get flightVisible(): boolean { return flightVisible },
    get isAlarmed(): boolean { return sim.alarmed },
    get isSleeping(): boolean { return sim.asleep },
    get isAtDoor(): boolean { return sim.atDoor },
    get isGoingHome(): boolean { return sim.home !== null },
    pushSim(dx: number, dz: number): void {
      if (options.flier || sold) return
      pushAnimalSim(sim, dx, dz)
    },
    setHomeTrip(door, speedScale = 1): void {
      if (options.flier) return
      const scale = door && Number.isFinite(speedScale) && speedScale > 0 ? speedScale : 1
      sim.home = door ? { x: door.x, z: door.z, speedScale: scale } : null
      sim.atDoor = false
      if (loaded) loaded.mixer.timeScale = scale > 1 ? Math.min(3, scale) : alarmScale()
    },
    facePoint(x, z): void { sim.heading = headingOf(x - sim.x, z - sim.z) },
    get isEating(): boolean { return eating },
    setEating(next, holding = false): void {
      eating = next
      holdingBerry = next && holding
      if (heldBerry) heldBerry.scale.setScalar(holdingBerry ? 1 : 0.0001)
    },
    setPursuit(next): void {
      if (options.flier) return
      setSimPursuit(sim, next)
    },
    placeAt(x: number, z: number): void {
      if (options.flier) return
      sim.visit.route = null
      wrapper.position.x = x
      wrapper.position.z = z
      snapAnimalSim(sim, x, z)
      if (options.groundSampler) {
        groundYCurrent = options.groundY + options.groundSampler(x, z)
        wrapper.position.y = groundYCurrent
      }
      sim.paused = 0
      sim.nextDecision = 0
      wrapper.updateMatrixWorld(true)
    },
    setSleepSpot(spot: SleepSpot | null): void {
      if (options.flier) return
      sim.sleepSpot = spot
      if (!spot) sim.asleep = false
    },
    setAlarmed(next: boolean): void {
      if (options.flier || sim.alarmed === next) return
      setSimAlarmed(sim, next)
      shownAlarmed = next
      if (loaded) loaded.mixer.timeScale = next ? 2.2 : 1
    },
    setFlightPose(pose: FlightPose): void {
      if (!options.flier || sold) return
      flightVisible = pose.visible
      wrapper.position.set(pose.x, pose.y, pose.z)
      wrapper.rotation.set(0, pose.heading, 0)
      if (!capture) posePivot.rotation.z = pivotBaseRotation.z - pose.pitch
      flightRate = pose.rate
      if (!capture) posePivot.scale.setScalar(Math.max(0.35, pose.puff ?? 1))
      if (loaded) loaded.mixer.timeScale = flightRate
      setAnimation(pose.clip, 0.3)
    },
    get isLoose(): boolean { return Boolean(options.isLoose?.()) },
    get animationPhase(): number { return stableAnimationPhase },
    setDetailedVisible(visible: boolean): void {
      detailActive = visible && !sold && sim.stage > 0
      if (detailActive) {
        if (loaded && modelRoot && modelRoot.parent !== posePivot) posePivot.add(modelRoot)
        void ensureDetailedModel()
        if (loaded) {
          const action = loaded.actions.get(active)
          if (action && loaded.activeAnimation !== active) {
            action.reset().play()
            loaded.activeAnimation = active
            loaded.mixer.setTime(elapsed)
          }
        }
      } else if (loaded && modelRoot?.parent === posePivot && !capture) {
        posePivot.remove(modelRoot)
      }
      wrapper.visible = detailActive && Boolean(loaded || placeholderLoaded)
    },
    get canSell(): boolean {
      return canSellAnimal({ sold, captured, capturing: capture !== null || pendingCapture, stage: sim.stage, appearance })
    },
    sell(): boolean {
      if (!canSellAnimal({ sold, captured, capturing: capture !== null, stage: sim.stage, appearance })) return false
      sold = true
      focused = false
      wrapper.visible = false
      return true
    },
    get gltf(): AnimalGLTF | null { return gltf },
    eyeColor: options.eyeColor ?? BODY_MATERIALS[options.id].color.getHexString(),
    get heartEyeCount(): number { return countHeartEyes(modelRoot ?? posePivot) },
    setGrowth(scale: number): void {
      wrapper.scale.setScalar(Math.max(0.1, Math.min(1, scale)))
      sim.radius = options.size * BODY_RADIUS_SHARE * wrapper.scale.x
    },
    setAnimation,
    playOnce,
    setAppearance,
    beginCapture,
    get isCaptured(): boolean { return captured },
    get isCapturing(): boolean { return pendingCapture || Boolean(capture && !capture.finished) },
    get captureProgress(): number { return lastCaptureProgress },
    get appearance(): AnimalAppearance { return appearance },
    get isAtFarm(): boolean { return sim.visit.side === 'farm' && sim.visit.route === null },
    get isResidencyPending(): boolean { return sim.pendingStage !== null },
    get stage(): AnimalStage { return sim.stage },
    set stage(next: AnimalStage) { setStage(next) },
    update(deltaSeconds, simAlpha): void {
      if (sold) return
      const delta = Math.min(deltaSeconds, 0.05)
      elapsed += delta
      // A hunt can panic or calm the sim record, or end a pursuit, between frames: let the clips follow.
      if (sim.alarmed !== shownAlarmed) {
        shownAlarmed = sim.alarmed
        if (loaded) loaded.mixer.timeScale = shownAlarmed ? 2.2 : 1
      }
      if (sim.pursuit) wasPursuing = true
      else if (wasPursuing) {
        wasPursuing = false
        if (loaded) loaded.mixer.timeScale = alarmScale()
      }
      // Show the sim's position, blended between the last two ticks. A flier's is placed by
      // its pose, and a held animal (the reveal) has a record that stands still.
      if (!options.flier) {
        interpolateAnimalSim(sim, simAlpha, blended)
        wrapper.position.x = blended.x
        wrapper.position.z = blended.z
      }
      if (capture) {
        if (!loaded && !placeholderLoaded && !modelLoading) void ensureDetailedModel()
        if (detailActive) loaded?.mixer.update(delta)
        const pose = capture.update(delta)
        posePivot.rotation.set(
          pivotBaseRotation.x + pose.roll,
          pivotBaseRotation.y + pose.yaw,
          pivotBaseRotation.z + pose.pitch,
        )
        posePivot.position.set(pivotBasePosition.x, pivotBasePosition.y + pose.lift, pivotBasePosition.z)
        lastCaptureProgress = pose.fill
        if (captureVerticalRange) setAnimalAppearanceProgress(modelRoot ?? posePivot, pose.fill, captureVerticalRange)
        if (capture.finished) {
          capture.dispose()
          capture = null
          captureVerticalRange = null
          lastCaptureProgress = 1
          focused = false
          releaseHold()
          posePivot.position.copy(pivotBasePosition)
          posePivot.rotation.copy(pivotBaseRotation)
        }
        return
      }

      if (options.flier) {
        // The predator sim owns the position; all that is left is the wing cycle.
        if (loaded) loaded.mixer.timeScale = flightRate
        if (detailActive) loaded?.mixer.update(delta)
        return
      }

      // A bed beats a gate walk and wandering, but a walk to a door or a pursuit beats the bed.
      const sleeping = Boolean(sim.sleepSpot) && !sim.home && !sim.pursuit
      if (sleepBlend > 0 && !sleeping && !sim.home && !sim.pursuit) {
        // Waking: stand back up before wandering off.
        if (loaded) loaded.mixer.timeScale = sim.alarmed ? 2.2 : 1
        applySleepPose(delta)
      }

      // Wandering, the walks, the bed and the hunts are the sim's (tickSim); the view has
      // blended the last two ticks above, and now turns to face the way it is going and picks the clip.
      // A hunt or a visitor's walk steers by pursuit: a lunge snaps round to face
      // the prey, a stalk turns smoothly, and creeping is a slow ripple of the body.
      const pursuit = sim.home ? null : sim.pursuit
      if (oneShotLeft > 0) {
        // A one-shot (the lion's strike) owns the clip; it still snaps round to face the sim's heading.
        oneShotLeft = Math.max(0, oneShotLeft - delta)
        facingScratch.setFromAxisAngle(upAxis, sim.heading)
        wrapper.quaternion.slerp(facingScratch, 1 - Math.exp(-14 * delta))
        if (loaded) loaded.mixer.timeScale = 1
      } else if (sim.motion === 'walk') {
        facingScratch.setFromAxisAngle(upAxis, sim.heading)
        const turnRate = pursuit ? (pursuit.speedScale > 3 ? 14 : 5) : 4.5
        wrapper.quaternion.slerp(facingScratch, 1 - Math.exp(-turnRate * delta))
        setAnimation('WALK', pursuit ? 0.18 : 0.24)
        if (pursuit && loaded) loaded.mixer.timeScale = Math.max(0.6, pursuit.speedScale * 0.55)
      } else if (sim.motion === 'sleep') {
        // Lying in bed: turn to the bed's heading, slowly. There is no sleep clip for most
        // species: the model keeps its IDLE pose, slowed, and the pivot is squashed into a crouch.
        facingScratch.setFromAxisAngle(upAxis, sim.heading)
        wrapper.quaternion.slerp(facingScratch, 1 - Math.exp(-3 * delta))
        setAnimation(hasSleepClip() ? 'SLEEP' : 'IDLE', 0.6)
      } else if (sim.motion === 'idle') {
        if (eating) {
          facingScratch.setFromAxisAngle(upAxis, sim.heading)
          wrapper.quaternion.slerp(facingScratch, 1 - Math.exp(-5 * delta))
        }
        setAnimation(eating && loaded?.actions.has('EAT') ? 'EAT' : 'IDLE', 0.18)
        if (pursuit && loaded) loaded.mixer.timeScale = eating ? 1 : 0.6
      }
      if (sleeping) {
        if (loaded) loaded.mixer.timeScale = sim.asleep ? (hasSleepClip() ? 0.5 : 0.3) : 1
        applySleepPose(delta)
      }

      if (detailActive) loaded?.mixer.update(delta)
      // Ride the deformed garden: ease toward the sampled terrain height so
      // both walking and idling animals follow digs and deposits.
      if (options.groundSampler) {
        const targetY = options.groundY + options.groundSampler(wrapper.position.x, wrapper.position.z)
        groundYCurrent += (targetY - groundYCurrent) * (1 - Math.exp(-8 * delta))
        wrapper.position.y = groundYCurrent + (loaded ? 0 : Math.abs(Math.sin(elapsed * 5.8)) * 0.035)
      } else if (!loaded) {
        wrapper.position.y = options.groundY + Math.abs(Math.sin(elapsed * 5.8)) * 0.035
        wrapper.quaternion.setFromAxisAngle(upAxis, Math.sin(elapsed * 2.7) * 0.04)
      }
      wrapper.updateMatrixWorld(true)
    },
    dispose(): void {
      capture?.dispose()
      capture = null
      captureVerticalRange = null
      clearHeartEyes(modelRoot ?? posePivot)
      options.canvas.removeEventListener('pointerdown', onPointerDown)
      wrapper.parent?.remove(wrapper)
      wrapper.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return
        disposeTemporaryMaterials(object)
      })
      // Each clone owns its skeleton's bone texture; the geometry is the species'.
      if (loaded) skeletonsOf(loaded.root).forEach((skeleton) => skeleton.dispose())
      loaded?.mixer.stopAllAction()
    },
  }
}
